/** Wireframe / terminal look for Sliding Puzzle Quest. */
export const mono = '"Consolas", "Courier New", monospace';

export const slidingBg = "#ffffff";
export const slidingText = "#0f1419";
export const slidingTextMuted = "rgba(15, 23, 42, 0.65)";
export const slidingTextSubtle = "rgba(15, 23, 42, 0.55)";
export const slidingAccent = "#3BA666";
export const slidingAccentHover = "#56c97f";

export const wireBorderColor = "rgba(15, 23, 42, 0.35)";

export const wireBorder = `1px solid ${wireBorderColor}`;

export const pageSx = {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    px: 2,
    py: 3,
    fontFamily: mono,
    bgcolor: slidingBg,
    color: slidingText,
};

export const sectionSx = {
    border: wireBorder,
    borderRadius: 0,
    py: 2.5,
    px: 2,
    mb: 0,
    textAlign: "center",
    bgcolor: slidingBg,
};

/** Choose-grid / form panels (left-aligned content). */
export const wirePanelSx = {
    ...sectionSx,
    textAlign: "left",
    width: "100%",
    maxWidth: 360,
    mx: "auto",
    boxSizing: "border-box",
};

export const wireDividerSx = {
    border: "none",
    borderTop: wireBorder,
    my: 2,
};

export const sectionTitleSx = {
    fontFamily: mono,
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(15, 23, 42, 0.9)",
    mb: 2,
    textAlign: "left",
};

export const wireButtonSx = {
    fontFamily: mono,
    fontSize: "0.95rem",
    fontWeight: 600,
    color: slidingText,
    textTransform: "none",
    border: wireBorder,
    borderRadius: 0,
    py: 1.25,
    px: 2,
    width: "100%",
    maxWidth: 320,
    justifyContent: "center",
    bgcolor: slidingBg,
    "&:hover": {
        borderColor: slidingAccent,
        bgcolor: "rgba(59,166,102,0.12)",
    },
    "&.Mui-disabled": {
        color: "rgba(15, 23, 42, 0.35)",
        borderColor: "rgba(15, 23, 42, 0.2)",
    },
};

export const captionSx = {
    fontFamily: mono,
    fontSize: "0.8rem",
    color: slidingTextMuted,
    mt: 1,
};

export const difficultyRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    mt: 1.25,
    flexWrap: "wrap",
    fontFamily: mono,
    fontSize: "0.8rem",
};

export const navRowSx = {
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    gap: 0,
    width: "100%",
    maxWidth: 320,
    mx: "auto",
};

export const navItemSx = (active) => ({
    fontFamily: mono,
    fontSize: "0.8rem",
    fontWeight: active ? 700 : 500,
    color: active ? slidingAccent : slidingTextMuted,
    textTransform: "none",
    flex: 1,
    py: 1.25,
    px: 1,
    borderRadius: 0,
    border: "1px solid",
    borderColor: active ? "rgba(59,166,102,0.65)" : "rgba(15, 23, 42, 0.25)",
    bgcolor: active ? "rgba(59,166,102,0.1)" : slidingBg,
    "&:hover": {
        bgcolor: "rgba(59,166,102,0.12)",
        borderColor: "rgba(59,166,102,0.5)",
    },
    "&:not(:first-of-type)": {
        borderLeft: "none",
    },
});

export const difficultyDotSx = (active) => ({
    fontFamily: mono,
    fontSize: "0.8rem",
    fontWeight: active ? 700 : 400,
    color: active ? slidingAccent : slidingTextSubtle,
    cursor: "pointer",
    border: "none",
    bgcolor: "transparent",
    p: 0.25,
    minWidth: 0,
    textTransform: "none",
    "&:hover": {
        color: active ? slidingAccentHover : "rgba(15, 23, 42, 0.85)",
    },
});

/** GRID / LEVEL wireframe row — label + bracket options. */
export const optionRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    flexWrap: "wrap",
    fontFamily: mono,
    fontSize: "0.85rem",
    mb: 1.5,
};

export const optionLabelSx = {
    fontFamily: mono,
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: slidingTextMuted,
    minWidth: "3.25rem",
    textAlign: "right",
};

export const wireOptionSx = (selected, compact = false) => ({
    fontFamily: mono,
    fontSize: compact ? "0.72rem" : "0.85rem",
    fontWeight: selected ? 700 : 400,
    color: selected ? slidingAccent : slidingTextSubtle,
    border: "none",
    bgcolor: "transparent",
    p: 0.5,
    minWidth: 0,
    textTransform: "none",
    letterSpacing: "0.04em",
    "&:hover": {
        color: selected ? slidingAccentHover : slidingText,
    },
});

export const playButtonSpacedSx = {
    ...wireButtonSx,
    letterSpacing: "0.42em",
    fontSize: "1rem",
    py: 1.5,
    mt: 0.5,
};

/** Compact GRID / LEVEL rows inside library cards. */
export const cardOptionRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 0.5,
    flexWrap: "wrap",
    fontFamily: mono,
    fontSize: "0.72rem",
    mb: 0.5,
};

export const cardOptionLabelSx = {
    fontFamily: mono,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: slidingTextMuted,
    minWidth: "2.6rem",
    flexShrink: 0,
};

export const tabRowSx = {
    display: "flex",
    width: "100%",
    maxWidth: 360,
    mx: "auto",
    mb: 2,
    flexShrink: 0,
};

export const tabItemSx = (active) => ({
    fontFamily: mono,
    fontSize: "0.8rem",
    fontWeight: active ? 700 : 500,
    flex: 1,
    py: 1,
    color: active ? slidingAccent : slidingTextMuted,
    textTransform: "none",
    borderRadius: 0,
    border: "1px solid",
    borderColor: active ? "rgba(59,166,102,0.65)" : "rgba(15, 23, 42, 0.25)",
    bgcolor: active ? "rgba(59,166,102,0.1)" : slidingBg,
    "&:hover": {
        bgcolor: "rgba(59,166,102,0.12)",
        borderColor: "rgba(59,166,102,0.5)",
    },
    "&:not(:first-of-type)": {
        borderLeft: "none",
    },
});
